import axios from "../../config/axiosClient";
import Config from "../../config/Config.js";


// --------------------------------------------
// core authentication functionality
// --------------------------------------------

// hits API to authenticate user
const SIGN_IN = (creds) => {
  return axios
    .post(`${Config().signinUrl}`, {
      email: creds.email,
      password: creds.password,
    })
    .then((res) => {
      const token = res.data.key;
      return GET_USER_DATA_AFTER_API_AUTH(token, "/home");
    })
    .catch((err) => {
      let message = "Authentication failed. Please check your credentials and try again.";
      if (err.response?.data) {
        const errMsgs = err.response.data;
        const keys = Object.keys(errMsgs);
        if (keys.length > 0) {
          // Flatten all error messages into a single string
          message = keys
            .map((k) => {
              const val = errMsgs[k];
              return Array.isArray(val) ? val.join(" ") : val;
            })
            .join(" ");
        }
      }
      throw new Error(message);
    });
};


// Gets user data upon authentication
const GET_USER_DATA_AFTER_API_AUTH = (token, redirect_to) => {
  return axios
    .get(Config().userAuthUrl, {
      headers: { Authorization: `Token ${token}` },
    })
    .then((res) => {
      const user_data = res.data;

      localStorage.setItem("TOKEN", token);
      localStorage.setItem("USER_PK", user_data.id || user_data.pk);
      localStorage.setItem("USERNAME", user_data.username);
      localStorage.setItem("USER_EMAIL", user_data.email);
      localStorage.setItem("USER_FIRST_NAME", user_data.first_name);
      localStorage.setItem("USER_LAST_NAME", user_data.last_name);
      localStorage.setItem("IS_AUTHENTICATED", true);
      window.location = redirect_to;
    })
    .catch(() => {
      throw new Error("Failed to retrieve user data after authentication.");
    });
};


// creates new user, prompts user to confirm email
const SIGN_UP = (creds) => {
  return axios
    .post(`${Config().signupUrl}`, {
      username: creds.username,
      email: creds.email,
      password1: creds.password1,
      password2: creds.password2,
    })
    .then((res) => {
      const token = res.data.key;
      if (token !== null) {
        localStorage.setItem("USER_EMAIL", creds.email);
        window.location = "/accounts/confirm";
      }
    })
    .catch((err) => {
      let message = "Sign up failed. Please check your information and try again.";
      if (err.response?.data) {
        const errMsgs = err.response.data;
        const keys = Object.keys(errMsgs);
        if (keys.length > 0) {
          message = keys
            .map((k) => {
              const val = errMsgs[k];
              return Array.isArray(val) ? val.join(" ") : val;
            })
            .join(" ");
        }
      }
      throw new Error(message);
    });
};


// clears localStorage, signing out user
const SIGN_OUT = (token) => {
  axios
    .post(
      `${Config().signoutUrl}`,
      {},
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    )
    .catch(() => {})
    .finally(() => {
      localStorage.clear();
      localStorage.setItem("IS_AUTHENTICATED", false);
      window.location.href = "/";
    });
};


// resets password, step 1/2
const PASSWORD_RESET_1 = (email) => {
  return axios
    .post(`${Config().pwdResetUrl}`, { email })
    .then((res) => {
      alert(res.data.detail || "Password reset email sent successfully!");
    })
    .catch((err) => {
      if (err.response?.status === 500) {
        alert("Server error occurred. Please contact support.");
      } else if (err.response?.status === 400) {
        alert(err.response.data?.email?.[0] || "Invalid email address.");
      } else if (err.response?.data?.detail) {
        alert(err.response.data.detail);
      } else {
        alert("Failed to send password reset email. Please try again.");
      }
    });
};

// resets password, step 2/2
const PASSWORD_RESET_2 = (new_password1, new_password2, uid, token) => {
  return axios
    .post(`${Config().pwdResetConfirmUrl}`, {
      new_password1,
      new_password2,
      uid,
      token,
    })
    .then((res) => {
      alert(res.data.detail);
    })
    .catch((err) => {
      if (err.response?.status === 400) {
        alert(err.response.data?.detail || "Invalid password reset data. Please try again.");
      } else if (err.response?.status === 500) {
        alert("Server error occurred during password reset.");
      } else {
        alert("Password reset failed. Please try again.");
      }
    });
};


// determines authentication data from localStorage
const RESOLVE_LOCAL_AUTH_STATE = () => {
  return {
    is_authenticated: localStorage.getItem("IS_AUTHENTICATED"),
    token: localStorage.getItem("TOKEN"),
    username: localStorage.getItem("USERNAME"),
  };
};


const RESOLVE_LOCAL_USER_STATE = () => {
  return {
    username: localStorage.getItem("USERNAME"),
    pk: localStorage.getItem("USER_PK"),
    email: localStorage.getItem("USER_EMAIL"),
    first_name: localStorage.getItem("USER_FIRST_NAME"),
    last_name: localStorage.getItem("USER_LAST_NAME"),
  };
};

// Validates current token against the API. Clears storage if invalid.
const VALIDATE_TOKEN = async () => {
  const auth = RESOLVE_LOCAL_AUTH_STATE();
  if (auth.is_authenticated !== "true" || !auth.token) return false;

  try {
    await axios.get(Config().userAuthUrl, {
      headers: { Authorization: `Token ${auth.token}` },
    });
    return true;
  } catch (err) {
    if (err.response?.status === 401) {
      localStorage.clear();
      localStorage.setItem("IS_AUTHENTICATED", "false");
      return false;
    }
    // Network error or server error — don't log out
    return true;
  }
};

export {
  SIGN_UP,
  SIGN_IN,
  SIGN_OUT,
  PASSWORD_RESET_1,
  PASSWORD_RESET_2,
  RESOLVE_LOCAL_AUTH_STATE,
  RESOLVE_LOCAL_USER_STATE,
  VALIDATE_TOKEN,
};
